import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowRight, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setPendingApproval(false);
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
      if (result.error?.includes('verify your email')) {
        setShowResend(true);
        setResendEmail(email);
      } else if (result.error?.includes('pending admin approval')) {
        setPendingApproval(true);
      }
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      setResendSent(true);
    } catch (err) { void 0; }
    setResendLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 max-w-[600px]">
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-8">
            <Zap className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={2.5} />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ZipAI</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Welcome back
          </h1>
          <p className="text-muted-foreground text-base">Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
          {pendingApproval && (
            <div className="text-sm bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-4 rounded-md" data-testid="pending-approval-notice">
              <p className="font-medium mb-1">Account pending approval</p>
              <p className="text-amber-300/80 text-xs leading-relaxed">Your email has been verified. An administrator will review your account shortly. You will receive an email notification once approved.</p>
            </div>
          )}
          {error && !pendingApproval && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-md" data-testid="login-error">
              {error}
              {showResend && !resendSent && (
                <div className="mt-3 pt-3 border-t border-destructive/20">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="flex items-center gap-2 text-foreground font-medium hover:underline"
                    data-testid="resend-verification-btn"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {resendLoading ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
              )}
              {resendSent && (
                <div className="mt-3 pt-3 border-t border-destructive/20 text-[hsl(var(--success))]">
                  Verification email sent! Check your inbox.
                </div>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@zipai.com"
              required
              data-testid="login-email-input"
              className="h-11 rounded-[4px]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="login-password-input"
              className="h-11 rounded-[4px]"
            />
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-11 rounded-[4px] font-semibold active:scale-[0.98] transition-transform" data-testid="login-submit-btn">
            {loading ? 'Signing in...' : 'Sign in'}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        <p className="mt-8 text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-foreground font-medium hover:underline" data-testid="register-link">Create one</Link>
        </p>

        <p className="mt-16 text-xs text-muted-foreground/60 tracking-wide text-center">Powered By Cuvano&reg;</p>
      </div>

      <div className="hidden lg:block flex-1 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1640346876473-f76a73c71539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3NzQ5NTc3OTJ8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </div>
  );
}
